// src/pages/ClientList.jsx
import React, { useEffect, useMemo, useState } from "react";
import Axios from "../utils/Axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Nav from "../components/Nav";
import "../styles/ClientList.css";
import Toast from "../utils/Toast";

import AuthHandler from "../utils/AuthHandler";
import validateForm, { IsValidEmail } from "../utils/Validation";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_CLIENTS_REQUEST } from "../store/Actions/Actions";
import {
  ADD_CLIENT,
  CLIENT_DETAILS,
  DELETE_CLIENT,
  EDIT_CLIENT,
} from "../config/API_Collection";
import AccessRestricted from "../components/AccessRestricted";
import Dropdown from 'react-bootstrap/Dropdown';
import { forwardRef } from 'react';

// Reusable full-page loader (keeps layout steady)
function FullPageLoaderLayout() {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="top-loading-bar" />
        <div className="project-list-container">
          <div className="loading-wrapper">
            <div className="spinner-border text-secondary" role="status" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Restricted layout (post-bootstrap deny)
function RestrictedLayout({ homePath = "projects" }) {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="h-100">
          <AccessRestricted homePath={homePath} />
        </div>
      </div>
    </div>
  );
}

export default function ClientList() {
  const [toast, setToast] = useState({ message: "", type: "" });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100); // Default 100 clients per page
  const [checkUpdate, setCheckUpdate] = useState(true);

  // submit locks
  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);

  const navigate = useNavigate();
  const DISPATCH = useDispatch();

  // ---------- Auth/permissions ----------
  const login = useSelector((s) => s?.login);
  const actions = login?.actions || null;
  const isBootstrapping = !!login?.isBootstrapping;

  const canListClients = actions?.clients?.client_list === true;
  const canAddClient = actions?.clients?.add_client === true;
  const canEditClient = actions?.clients?.edit_client === true;
  const canDeleteClient = actions?.clients?.delete_client === true;

  // ---------- Data ----------
    const clients = useSelector((s) => s?.clientsList?.clients) || [];
  const isLoading = useSelector((s) => s?.clientsList?.isLoading);

  useEffect(() => {
    if (canListClients) {
      DISPATCH({ type: FETCH_CLIENTS_REQUEST });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DISPATCH, canListClients, checkUpdate]);

  // Options for status dropdown (built from current data)
  const statusOptions = useMemo(() => {
    const toText = (n) =>
      n === 1 ? "Active" : n === 4 ? "Blocked" : n === 3 ? "Inactive" : "";
    const set = new Set();
    clients.forEach((c) => {
      const t = toText(c?.status);
      if (t) set.add(t);
    });
    return Array.from(set).sort();
  }, [clients]);

  // ---------- Expanded filter (search + status) ----------
  const filteredClients = useMemo(() => {
    const term = (searchTerm || "").trim().toLowerCase();

    const statusToText = (n) =>
      n === 1 ? "active" : n === 4 ? "blocked" : n === 3 ? "inactive" : "";

    return clients.filter((c) => {
      // status filter
      if (selectedStatus) {
        const st = statusToText(c?.status);
        if (st !== selectedStatus.toLowerCase()) return false;
      }

      // search filter
      if (!term) return true;

      const name = (c?.client_name || "").toLowerCase();
      const email = (c?.email || "").toLowerCase();
      const phone = (c?.phone || "").toLowerCase();
      const address = (c?.address || "").toLowerCase();
      const created = (c?.created_at || "").toLowerCase();
      const status = statusToText(c?.status);

      const idRaw = String(c?.client_id ?? "");
      const idPretty = `clt${idRaw.padStart(3, "0")}`.toLowerCase();

      return (
        name.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        address.includes(term) ||
        created.includes(term) ||
        status.includes(term) ||
        idRaw.toLowerCase().includes(term) ||
        idPretty.includes(term)
      );
    });
  }, [clients, searchTerm, selectedStatus]);

  // ---------- Pagination ----------
  const totalPages =
    Math.ceil((filteredClients.length || 0) / rowsPerPage) || 1;
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentClients = filteredClients.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Build a condensed page list: show `1 2 3 ... last` when there are many pages
  const buildPageButtons = (current, total) => {
    if (total <= 3) return Array.from({ length: total }, (_, i) => i + 1);

    // Keep original behaviour for first two pages
    if (current <= 2) return [1, 2, "...", total];

    // If we're near the end, show the last three pages
    if (current >= total - 1) {
      const start = Math.max(1, total - 2);
      return Array.from({ length: total - start + 1 }, (_, i) => start + i);
    }

    // Default: show current as first, next page, ellipsis, last
    return [current, current + 1, "...", total];
  };
  const pageButtons = buildPageButtons(currentPage, totalPages);

  const showToast = (message, type) => setToast({ message, type });

  // pass client via state + session cache
  const clientDetail = (client) => {
    try {
      sessionStorage.setItem("client.preload", JSON.stringify(client));
    } catch {}
    navigate(`${CLIENT_DETAILS}${client?.client_id}`, { state: { client } });
  };

  // ---------- Modals ----------
  const [showModalAdd, setShowModalAdd] = useState(false);
  const handleOpenModalAdd = () => setShowModalAdd(true);
  const handleCloseModalAdd = () => {
    setShowModalAdd(false);
    setIsSubmittingAdd(false);
  };

  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalRemove, setShowModalRemove] = useState(false);
  const [selectedClientIdRemove, setSelectedClientIdRemove] = useState(null);

  const [editData, setEditData] = useState({
    client_id: "",
    client_name: "",
    email: "",
    phone: "",
    address: "",
  });

  const OpenModalEdit = (client_id, client_name, email, phone, address) => {
    setEditData({
      client_id,
      client_name: client_name || "",
      email: email || "",
      phone: phone || "",
      address: address || "",
    });
    setShowModalEdit(true);
  };
  const CloseModalEdit = () => {
    setShowModalEdit(false);
    setIsSubmittingEdit(false);
  };

  const OpenModalRemove = (client_id) => {
    setSelectedClientIdRemove(client_id);
    setShowModalRemove(true);
  };
  const CloseModalRemove = () => {
    setIsSubmittingDelete(false); // reset if dialog is closed
    setShowModalRemove(false);
  };

  // ---------- Form handlers with submit locks ----------
  const handleAddCompanie = async (e) => {
    e.preventDefault();
    if (isSubmittingAdd) return;
    setIsSubmittingAdd(true);

    try {
      const form = e.target;
      const payload = {
        client_name: form.client_name.value,
        email: form.email.value,
        phone: form.phone.value,
        address: form.address.value,
      };
      const required = ["client_name", "email", "phone", "address"];
      const errors = validateForm(payload, required);
      setFormErrors(errors);
      if (Object.keys(errors).length) {
        setIsSubmittingAdd(false);
        return;
      }
      if (!IsValidEmail(payload?.email)) {
        showToast("Enter Valid Email Address!", "failed");
        setIsSubmittingAdd(false);
        return;
      }

      const res = await Axios.post(ADD_CLIENT, payload);
      if (res.status === 200) {
        showToast("Client Added Successfully!", "success");
        setIsSubmittingAdd(false);
        setCheckUpdate((x) => !x);
        handleCloseModalAdd();
        return;
      }
      showToast(res?.data?.message || "Failed to add client", "failed");
      setIsSubmittingAdd(false);
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add client",
        "failed"
      );
      setIsSubmittingAdd(false);
    }
  };

  const handleEditCompanie = async (e) => {
    e.preventDefault();
    if (isSubmittingEdit) return;
    setIsSubmittingEdit(true);

    try {
      const form = e.target;
      const payload = {
        client_id: editData.client_id,
        client_name: form.client_name.value,
        email: form.email.value,
        phone: form.phone.value,
        address: form.address.value,
      };
      const required = ["client_name", "email", "phone", "address"];
      const errors = validateForm(payload, required);
      setFormErrors(errors);
      if (Object.keys(errors).length) {
        setIsSubmittingEdit(false);
        return;
      }

      const res = await Axios.post(EDIT_CLIENT, payload);
      if (res.status === 200) {
        showToast("Client Updated Successfully!", "success");
        setIsSubmittingEdit(false);
        CloseModalEdit();
        setCheckUpdate((x) => !x);
        return;
      }
      showToast(res?.data?.message || "Failed to update client", "failed");
      setIsSubmittingEdit(false);
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update client",
        "failed"
      );
      setIsSubmittingEdit(false);
    }
  };

  const handleRemoveCompanie = async () => {
    if (isSubmittingDelete) return;
    setIsSubmittingDelete(true);

    try {
      const payload = { client_id: selectedClientIdRemove };
      const res = await Axios.post(DELETE_CLIENT, payload);
      if (res.status === 200) {
        showToast("Client Deleted Successfully!", "success");
        CloseModalRemove();
        setCheckUpdate((x) => !x);
        return;
      }
      showToast(res?.data?.message || "Failed to delete client", "failed");
      setIsSubmittingDelete(false);
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete client",
        "failed"
      );
      setIsSubmittingDelete(false);
    }
  };

  // stop row navigation when interacting with dropdown
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (isBootstrapping) return <FullPageLoaderLayout />;
  if (!canListClients) return <RestrictedLayout homePath="projects" />;

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        {isLoading && <div className="top-loading-bar" />}
        <Toast message={toast.message} type={toast.type} />

        <div className="project-list-container">
          {/* FILTERS ROW — left: search + status, right: add button */}
          <div
            className="d-flex align-items-center mb-3"
            style={{ gap: 8, flexWrap: "nowrap", overflowX: "hidden" }}
          >
            {/* Left cluster */}
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: 320, flex: "0 0 320px" }}
              />
              <select
                className="form-select"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: 180, flex: "0 0 180px" }}
              >
                <option value="">All Status</option>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Spacer pushes button to the far right */}
            <div className="ms-auto" />

            {/* Right: Add button pinned to right edge */}
            {canAddClient && (
              <button
                className="btn-sm p-2 btn-primary d-flex align-items-center"
                onClick={handleOpenModalAdd}
                style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}
              >
                <i className="bi bi-plus-circle me-1" />
                Add New Client
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="loading-wrapper">
              <div className="spinner-border text-secondary" role="status" />
            </div>
          ) : (
            <>
              <div>
                <table className="table table-bordered custom-table">
                  <thead>
                    <tr>
                      <th>Client Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Status</th>
                      <th>Registered Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentClients.length > 0 ? (
                      currentClients.map((client) => (
                        <tr
                          key={client.client_id}
                          style={{ cursor: "pointer" }}
                        >
                          <td onClick={() => clientDetail(client)}>
                            {client.client_name}
                          </td>
                          <td onClick={() => clientDetail(client)}>
                            {client.email}
                          </td>
                          <td onClick={() => clientDetail(client)}>
                            {client.phone}
                          </td>
                          <td onClick={() => clientDetail(client)}>
                            {client.address}
                          </td>
                          <td onClick={() => clientDetail(client)}>
                            {client.status === 1
                              ? "Active"
                              : client.status === 4
                              ? "Blocked"
                              : client.status === 3
                              ? "Inactive"
                              : ""}
                          </td>
                          <td onClick={() => clientDetail(client)}>
                            {client.created_at}
                          </td>

                          <td className="text-end" onClick={stop} style={{ overflow: 'visible' }}>
                            <Dropdown align="end" renderOnMount>
                              <Dropdown.Toggle as={forwardRef((props, ref) => (
                                <button
                                  ref={ref}
                                  {...props}
                                  type="button"
                                  className="btn btn-sm"
                                  style={{ border: 'none', background: 'transparent', boxShadow: 'none' }}
                                  aria-label="More actions"
                                >
                                  <i className="bi bi-three-dots-vertical" />
                                </button>
                              ))} id={`dropdown-client-${client.client_id}`}/>
                              <Dropdown.Menu>
                                {canEditClient && (
                                  <Dropdown.Item
                                    onClick={(e) => {
                                      stop(e);
                                      OpenModalEdit(
                                        client.client_id,
                                        client.client_name,
                                        client.email,
                                        client.phone,
                                        client.address
                                      );
                                    }}
                                  >
                                    Edit Client
                                  </Dropdown.Item>
                                )}
                                {canDeleteClient && (
                                  <Dropdown.Item
                                    onClick={(e) => {
                                      stop(e);
                                      OpenModalRemove(client.client_id);
                                    }}
                                  >
                                    Remove Client
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No companies available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination (condensed numbered buttons) */}
              {filteredClients.length > rowsPerPage && (
                <div className="pagination-controls d-flex justify-content-center mt-3">
                  <button
                    className="btn btn-outline-primary mx-1"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>

                  {pageButtons.map((p, idx) =>
                    typeof p === "number" ? (
                      <button
                        key={idx}
                        className={`btn mx-1 ${
                          currentPage === p
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    ) : (
                      <button
                        key={idx}
                        className="btn mx-1 btn-outline-secondary"
                        disabled
                      >
                        …
                      </button>
                    )
                  )}

                  <button
                    className="btn btn-outline-primary mx-1"
                    onClick={() =>
                      handlePageChange(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
              {/* Rows per page selector - bottom right */}
              <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Show</span>
                <select
                  className="form-select"
                  style={{ width: '100px', fontSize: '13px' }}
                  value={rowsPerPage === filteredClients.length && filteredClients.length > 500 ? "all" : rowsPerPage}
                  onChange={(e) => {
                    if (e.target.value === "all") {
                      setRowsPerPage(filteredClients.length);
                    } else {
                      setRowsPerPage(Number(e.target.value));
                    }
                    setCurrentPage(1); // Reset to first page
                  }}
                >
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="all">All</option>
                </select>
              </div>
            </>
          )}

          {/* Add Client Modal */}
          {showModalAdd && canAddClient && (
            <div className="custom-project-modal">
              <div className="modal-backdrop fade show"></div>
              <div
                className="modal fade show"
                tabIndex="-1"
                aria-hidden="true"
                style={{ display: "block", zIndex: 1050 }}
              >
                <div
                  className="modal-dialog modal-lg mx-auto"
                  style={{ marginTop: "2%" }}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Add New Client</h5>
                    </div>
                    <div className="modal-body">
                      <div className="add-budget-container">
                        <form
                          noValidate
                          className="budget-form"
                          onSubmit={handleAddCompanie}
                        >
                          <fieldset
                            disabled={isSubmittingAdd}
                            style={{ border: 0, padding: 0, margin: 0 }}
                          >
                            <div className="form-row">
                              <div
                                className={`form-group flex-item ${
                                  formErrors.client_name ? "has-error" : ""
                                }`}
                              >
                                <label>Client Name *</label>
                                <input type="text" name="client_name" />
                              </div>
                            </div>
                            <div className="form-row">
                              <div
                                className={`form-group flex-item ${
                                  formErrors.email ? "has-error" : ""
                                }`}
                              >
                                <label>Email *</label>
                                <input type="email" name="email" />
                              </div>
                              <div
                                className={`form-group flex-item ${
                                  formErrors.phone ? "has-error" : ""
                                }`}
                              >
                                <label>Phone *</label>
                                <input
                                  type="text"
                                  name="phone"
                                  minLength={9}
                                  maxLength={12}
                                />
                              </div>
                              <div
                                className={`form-group flex-item ${
                                  formErrors.address ? "has-error" : ""
                                }`}
                              >
                                <label>Address *</label>
                                <input type="text" name="address" />
                              </div>
                            </div>

                            <div className="d-flex justify-content-center">
                              <button type="submit" className="btn btn-primary">
                                {isSubmittingAdd ? "Adding..." : "Add Client"}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={handleCloseModalAdd}
                              >
                                Cancel
                              </button>
                            </div>
                          </fieldset>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Client Modal */}
          {showModalEdit && canEditClient && (
            <div className="custom-project-modal">
              <div className="modal-backdrop fade show"></div>
              <div
                className="modal fade show"
                tabIndex="-1"
                aria-hidden="true"
                style={{ display: "block", zIndex: 1050 }}
              >
                <div
                  className="modal-dialog modal-lg mx-auto"
                  style={{ marginTop: "2%" }}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Update Client</h5>
                    </div>
                    <div className="modal-body">
                      <div className="add-budget-container">
                        <form
                          noValidate
                          className="budget-form"
                          onSubmit={handleEditCompanie}
                        >
                          <fieldset
                            disabled={isSubmittingEdit}
                            style={{ border: 0, padding: 0, margin: 0 }}
                          >
                            <div className="form-row">
                              <div
                                className={`form-group flex-item ${
                                  formErrors.client_name ? "has-error" : ""
                                }`}
                              >
                                <label>Client Name *</label>
                                <input
                                  type="text"
                                  name="client_name"
                                  defaultValue={editData.client_name}
                                />
                              </div>
                            </div>

                            <div className="form-row">
                              <div
                                className={`form-group flex-item ${
                                  formErrors.email ? "has-error" : ""
                                }`}
                              >
                                <label>Email *</label>
                                <input
                                  type="text"
                                  name="email"
                                  defaultValue={editData.email}
                                />
                              </div>
                              <div
                                className={`form-group flex-item ${
                                  formErrors.phone ? "has-error" : ""
                                }`}
                              >
                                <label>Phone *</label>
                                <input
                                  type="text"
                                  name="phone"
                                  defaultValue={editData.phone}
                                  minLength={9}
                                  maxLength={12}
                                />
                              </div>
                              <div
                                className={`form-group flex-item ${
                                  formErrors.address ? "has-error" : ""
                                }`}
                              >
                                <label>Address *</label>
                                <input
                                  type="text"
                                  name="address"
                                  defaultValue={editData.address}
                                />
                              </div>
                            </div>

                            <div className="d-flex justify-content-center">
                              <button type="submit" className="btn btn-primary">
                                {isSubmittingEdit
                                  ? "Updating..."
                                  : "Update Client"}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={CloseModalEdit}
                              >
                                Cancel
                              </button>
                            </div>
                          </fieldset>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Client Modal */}
          {showModalRemove && canDeleteClient && (
            <div className="custom-project-modal">
              <div className="modal-backdrop fade show"></div>
              <div
                className="modal fade show"
                tabIndex="-1"
                aria-hidden="true"
                style={{ display: "block", zIndex: 1050 }}
              >
                <div
                  className="modal-dialog  mx-auto"
                  style={{ marginTop: "15%" }}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Confirm Client Deletion</h5>
                    </div>
                    <div className="modal-body text-center">
                      <p>Are you sure you want to delete this Client?</p>
                    </div>
                    <div className="d-flex justify-content-center gap-5">
                      <button
                        className="btn btn-danger px-5"
                        onClick={handleRemoveCompanie}
                        disabled={isSubmittingDelete}
                      >
                        {isSubmittingDelete ? "Deleting..." : "Yes"}
                      </button>
                      <button
                        className="btn btn-secondary px-5"
                        onClick={CloseModalRemove}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
